import { useSearchParams } from 'react-router';

/**
 * A safe wrapper around react-router's useSearchParams that returns
 * a fallback URLSearchParams when the hook throws (e.g., when the
 * component is rendered outside a proper router context, such as
 * in the CMS page builder editor).
 */
export function useSafeSearchParams(): URLSearchParams {
  try {
    const [searchParams] = useSearchParams();
    return searchParams;
  } catch {
    return new URLSearchParams();
  }
}

/** Navigation options forwarded to react-router. `replace: true` avoids a history entry. */
export type SetSearchParamsOptions = { replace?: boolean };

/**
 * A safe wrapper around react-router's setSearchParams that no-ops instead of throwing
 * when called outside a router context (e.g. inside the CMS page builder's live preview).
 * Returns a setter that takes an updater callback over a COPY of the current params, so
 * callers never mutate the params object useSearchParams handed them directly.
 *
 * `options` is passed straight through and left undefined when omitted, so existing callers
 * keep react-router's default (push) rather than silently changing history behaviour.
 */
export function useSafeSetSearchParams(): (
  updater: (params: URLSearchParams) => void,
  options?: SetSearchParamsOptions
) => void {
  try {
    const [searchParams, setSearchParams] = useSearchParams();
    return (updater: (params: URLSearchParams) => void, options?: SetSearchParamsOptions) => {
      const next = new URLSearchParams(searchParams);
      updater(next);
      setSearchParams(next, options);
    };
  } catch {
    return () => {};
  }
}
